import React, { useState } from 'react'
import map from 'lodash/map'
import find from 'lodash/find'
import filter from 'lodash/filter'
import classnames from 'classnames'
import ActionDropdown from '@SiteComponents/ActionDropdown'
import Button from '@SiteComponents/Button'
import ProjectVersionRow from './ProjectVersionRow'
import getAllowLinkToEditor from '@Utils/GetAllowLinkToEditor'
import getFormattedDuration from '@Utils/GetFormattedDuration'
import getLanguageStatusForLanguagesView from '@Utils/GetLanguageStatusForLanguagesView'
import getProjectVersionDisplayKind from '@Utils/GetProjectVersionDisplayKind'
import getShouldAllowToAddLanguages from '@Utils/GetShouldAllowToAddLanguages'
import getShouldAllowToGenerateClips from '@Utils/GetShouldAllowToGenerateClips'
import getCleanDisplayTitle from '@Utils/GetCleanDisplayTitle'
import Magic from '@Images/magic_generate.svg'
import More from '@Images/projects_table_list_more.svg'
import Video from '@Images/video.svg'

import {
  IProjectVersion,
} from '@SiteModules/ProjectVersion/types'

import {
  ISubtitle,
} from '@SiteModules/Subtitle/types'

import {
  IOpenRenameProjectVersionPopupPayload,
  IOpenDeleteProjectVersionPopupPayload,
} from '@SiteModules/Popup/types'

import './ProjectVersionsTable.scss'

interface ProjectVersionsTableProps {
  readonly projectVersions: IProjectVersion[]
  readonly fullProjectVersionsView: boolean
  resetOpenedProject(): void
  setOpenedProjectVersionId(openedProjectVersionId: string): void
  handleOpenRenameProjectVersionPopup(payload: IOpenRenameProjectVersionPopupPayload): void
  handleOpenDeleteProjectVersionPopup(payload: IOpenDeleteProjectVersionPopupPayload): void
  handleOpenSetupProjectOrRenewSubscriptionPopup(projectVersionId: string, projectType?: string): void
}

interface ClipCardProps extends ProjectVersionsTableProps {
  readonly projectVersion: IProjectVersion
  readonly index: number
  readonly sourceThumbnailUrl?: string
  readonly duplicatedThumbnailUrl?: boolean
}

interface GeneratingClipCardProps {
  readonly index: number
  readonly sourceThumbnailUrl?: string
}

const sortByTitleNumeric = (a: IProjectVersion, b: IProjectVersion) =>
  a.attributes.title.localeCompare(
    b.attributes.title,
    undefined,
    { numeric: true, sensitivity: 'base' }
  )

const getTranscript = (projectVersion: IProjectVersion): ISubtitle | undefined =>
  find(projectVersion.subtitles, subtitle => subtitle.attributes.transcription) as ISubtitle | undefined

const getDoneLanguagesCount = (projectVersion: IProjectVersion): number =>
  filter(projectVersion.subtitles, subtitle => getLanguageStatusForLanguagesView(subtitle.attributes.stage)[0] === 'done').length

const getSourceProjectVersion = (projectVersions: IProjectVersion[]): IProjectVersion | undefined =>
  find(projectVersions, pv => getProjectVersionDisplayKind(pv.attributes) === 'source')

const getClipProjectVersions = (projectVersions: IProjectVersion[]): IProjectVersion[] =>
  filter(projectVersions, pv => pv.attributes.isRepurposed)

const getPendingClipProjectVersions = (projectVersions: IProjectVersion[]): IProjectVersion[] =>
  filter(projectVersions, projectVersion => getProjectVersionDisplayKind(projectVersion.attributes) === 'pending_clip')

const getReadyClipProjectVersions = (projectVersions: IProjectVersion[]): IProjectVersion[] =>
  filter(projectVersions, projectVersion => getProjectVersionDisplayKind(projectVersion.attributes) === 'ready_clip')

const openProjectVersionEditor = (projectVersion: IProjectVersion) => {
  const transcript = getTranscript(projectVersion)

  if (!transcript || !getAllowLinkToEditor(projectVersion.attributes.isDisabled, transcript.attributes)) { return }

  const urlParams = transcript.attributes.hasVoice ? '?openVoice=true' : ''
  window.location.href = `/projects/${projectVersion.attributes.projectId}/project_versions/${projectVersion.id}/subtitles/${transcript.id}/edit${urlParams}`
}

const openProjectVersionEditorFromKeyDown = (event: React.KeyboardEvent<HTMLDivElement>, projectVersion: IProjectVersion) => {
  if (event.key !== 'Enter' && event.key !== ' ') { return }

  event.preventDefault()
  openProjectVersionEditor(projectVersion)
}

const ClipCard: React.FC<ClipCardProps> = props => {
  const {
    projectVersion,
    index,
    sourceThumbnailUrl,
    duplicatedThumbnailUrl,
  } = props

  const [showDropdown, setShowDropdown] = useState(false)

  const {
    title,
    duration,
    thumbnailUrl,
  } = projectVersion.attributes

  const transcript = getTranscript(projectVersion)
  const cleanTitle = getCleanDisplayTitle(title)
  const allowLinkToEditor = !!transcript && getAllowLinkToEditor(projectVersion.attributes.isDisabled, transcript.attributes)
  const shouldAllowToAddLanguages = getShouldAllowToAddLanguages(projectVersion.attributes)
  const shouldAllowToDeleteProjectVersion = true
  const hasDropdownOptions = shouldAllowToAddLanguages || shouldAllowToDeleteProjectVersion
  const numberOfLanguages = projectVersion.subtitles.length
  const numberOfDoneLanguages = getDoneLanguagesCount(projectVersion)
  const thumbnailIsFallback = !!thumbnailUrl && (thumbnailUrl === sourceThumbnailUrl || !!duplicatedThumbnailUrl)
  const showThumbnail = !!thumbnailUrl && !thumbnailIsFallback
  const showFallbackPreview = !showThumbnail && !!sourceThumbnailUrl
  const moreElementId = `project_version_card_more_${index}`
  const classNameForClickOutsideListenerMoreDropdown = `ProjectVersionsTable__action-dropdown-element-${index}`
  const testIdSuffix = `clip-${projectVersion.id}`

  const openEditor = () => {
    openProjectVersionEditor(projectVersion)
  }

  const openEditorFromKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    openProjectVersionEditorFromKeyDown(event, projectVersion)
  }

  const openEditorFromButton = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    openProjectVersionEditor(projectVersion)
  }

  const openLanguages = () => {
    props.resetOpenedProject()
    props.setOpenedProjectVersionId(projectVersion.id)
  }

  const toggleDropdown = () => {
    if (!hasDropdownOptions) { return }

    setShowDropdown(!showDropdown)
  }

  const handleOpenDeleteProjectVersionPopup = () => {
    props.handleOpenDeleteProjectVersionPopup({ projectVersionIds: [projectVersion.id] })
    setShowDropdown(false)
  }

  const handleOpenSetupProjectOrRenewSubscriptionPopup = () => {
    if (!shouldAllowToAddLanguages) { return }

    props.handleOpenSetupProjectOrRenewSubscriptionPopup(projectVersion.id)
    setShowDropdown(false)
  }

  const handleOpenRenameProjectVersionPopup = () => {
    props.handleOpenRenameProjectVersionPopup({ projectVersionId: projectVersion.id })
    setShowDropdown(false)
  }

  const actionDropdownList = (
    <ul>
      {shouldAllowToAddLanguages && (
        <li className="ProjectVersionsTable__action-dropdown-item">
          <button
            type="button"
            className="ProjectVersionsTable__action-dropdown-button"
            onClick={handleOpenSetupProjectOrRenewSubscriptionPopup}
            data-testid={`project-version-add-languages-${testIdSuffix}`}
          >
            <span className="ProjectVersionsTable__action-dropdown-item-text">
              Add languages
            </span>
          </button>
        </li>
      )}
      <li className="ProjectVersionsTable__action-dropdown-item">
        <button
          type="button"
          className="ProjectVersionsTable__action-dropdown-button"
          onClick={handleOpenRenameProjectVersionPopup}
          data-testid={`project-version-rename-${testIdSuffix}`}
        >
          <span className="ProjectVersionsTable__action-dropdown-item-text">
            Rename
          </span>
        </button>
      </li>
      <li className="ProjectVersionsTable__action-dropdown-item">
        <button
          type="button"
          className="ProjectVersionsTable__action-dropdown-button"
          onClick={handleOpenDeleteProjectVersionPopup}
          data-testid={`project-version-delete-${testIdSuffix}`}
        >
          <span className="ProjectVersionsTable__action-dropdown-item-text">
            Delete clip
          </span>
        </button>
      </li>
    </ul>
  )

  const renderActionDropdown = () => (
    <ActionDropdown
      showDropdown={showDropdown}
      dropdownList={actionDropdownList}
      classNamesForListContainer="ProjectVersionsTable__action-dropdown ProjectVersionsView__element"
      classNameForClickOutsideListener={classNameForClickOutsideListenerMoreDropdown}
      classNameOfScrollContainer="ProjectVersionsView"
      idOfDropdownElement={moreElementId}
      idOfPortalContainer="dashboard"
      dropdownOffsetX={42}
      dropdownOffsetY={4}
      dropdownWidth={209}
      setShowDropdown={setShowDropdown}
    />
  )

  return (
    <article
      className="ProjectVersionsTable__clip-card"
      style={{ animationDelay: `${Math.min(index, 8) * 70}ms` }}
    >
      <div
        className={classnames(
          'ProjectVersionsTable__clip-media',
          { 'ProjectVersionsTable__clip-media-clickable': allowLinkToEditor }
        )}
        onClick={allowLinkToEditor ? openEditor : undefined}
        onKeyDown={allowLinkToEditor ? openEditorFromKeyDown : undefined}
        role={allowLinkToEditor ? 'button' : undefined}
        tabIndex={allowLinkToEditor ? 0 : undefined}
        data-testid={allowLinkToEditor ? `project-version-open-editor-card-${testIdSuffix}` : undefined}
      >
        {showThumbnail ? (
          <img src={thumbnailUrl} alt="" loading="lazy" />
        ) : showFallbackPreview ? (
          <React.Fragment>
            <img
              className="ProjectVersionsTable__clip-preview-fallback-image"
              src={sourceThumbnailUrl}
              alt=""
              loading="lazy"
            />
            <span className="ProjectVersionsTable__clip-preview-fallback-overlay" aria-hidden="true" />
          </React.Fragment>
        ) : (
          <span className="ProjectVersionsTable__clip-placeholder" aria-hidden="true">
            <Video />
          </span>
        )}
        {!showThumbnail && (
          <span className="ProjectVersionsTable__clip-fallback-label">
            Generating preview...
          </span>
        )}
        {showThumbnail && (
          <React.Fragment>
            <span className="ProjectVersionsTable__clip-title-gradient" aria-hidden="true" />
            <span className="ProjectVersionsTable__clip-title-overlay">
              {cleanTitle}
            </span>
          </React.Fragment>
        )}
        {allowLinkToEditor && (
          <div className="ProjectVersionsTable__clip-overlay">
            <Button
              caption="Open editor"
              styleType="brand"
              size="small"
              onClick={openEditorFromButton}
              data-testid={`project-version-open-editor-button-${testIdSuffix}`}
            />
          </div>
        )}
      </div>
      <div className="ProjectVersionsTable__clip-details">
        <div className="ProjectVersionsTable__clip-title-row">
          <span className="ProjectVersionsTable__clip-title-spacer" aria-hidden="true" />
          {!showThumbnail && (
            <span className="ProjectVersionsTable__clip-title-sr-only">
              {cleanTitle}
            </span>
          )}
          <div
            className={classnames(
              'ProjectVersionsTable__clip-more',
              { 'ProjectVersionsTable__clip-more-disabled': !hasDropdownOptions }
            )}
          >
            <div
              className={`ProjectVersionsTable__clip-more-wrapper ${classNameForClickOutsideListenerMoreDropdown}`}
              id={moreElementId}
            >
              <More
                onClick={toggleDropdown}
                data-testid={`project-version-more-actions-${testIdSuffix}`}
              />
              {showDropdown && renderActionDropdown()}
            </div>
          </div>
        </div>
        <div className="ProjectVersionsTable__clip-meta">
          <span>{getFormattedDuration(duration)}</span>
          <button
            type="button"
            className="ProjectVersionsTable__clip-languages"
            onClick={openLanguages}
            data-testid={`project-version-open-languages-${testIdSuffix}`}
          >
            {numberOfDoneLanguages}/{numberOfLanguages} languages
          </button>
        </div>
      </div>
    </article>
  )
}

const GeneratingClipCard: React.FC<GeneratingClipCardProps> = props => {
  const { index, sourceThumbnailUrl } = props

  return (
    <article
      className="ProjectVersionsTable__clip-card ProjectVersionsTable__clip-card-generating"
      aria-busy="true"
      style={{ animationDelay: `${Math.min(index, 8) * 120}ms` }}
    >
      <div className="ProjectVersionsTable__generating-media">
        {sourceThumbnailUrl && (
          <React.Fragment>
            <img
              className="ProjectVersionsTable__generating-preview-image"
              src={sourceThumbnailUrl}
              alt=""
              loading="lazy"
            />
            <span className="ProjectVersionsTable__generating-preview-overlay" aria-hidden="true" />
          </React.Fragment>
        )}
        <span className="ProjectVersionsTable__generating-icon" aria-hidden="true">
          <Magic />
        </span>
        <span className="ProjectVersionsTable__generating-title">
          AI is crafting a new clip...
        </span>
        <span className="ProjectVersionsTable__generating-copy">
          <span>Hunting for viral hooks...</span>
          <span>Auto-framing faces...</span>
          <span>Generating captions...</span>
        </span>
      </div>
    </article>
  )
}

const ProjectVersionsTable: React.FC<ProjectVersionsTableProps> = props => {
  const { projectVersions, fullProjectVersionsView } = props

  const original = getSourceProjectVersion(projectVersions)
  const sourceThumbnailUrl = original?.attributes.thumbnailUrl

  const clips = getClipProjectVersions(projectVersions)
  const pendingClips = getPendingClipProjectVersions(clips)
    .slice()
    .sort((a, b) => (a.attributes.createdAt || '').localeCompare(b.attributes.createdAt || '') || sortByTitleNumeric(a, b))
  const readyClips = getReadyClipProjectVersions(clips)
    .slice()
    .sort(sortByTitleNumeric)
  const duplicatedThumbnailUrls = readyClips.reduce<Record<string, number>>((thumbnailUrls, clip) => {
    const { thumbnailUrl } = clip.attributes

    if (!thumbnailUrl) { return thumbnailUrls }

    return {
      ...thumbnailUrls,
      [thumbnailUrl]: (thumbnailUrls[thumbnailUrl] || 0) + 1,
    }
  }, {})

  const shouldAllowGeneratingClips = original ? getShouldAllowToGenerateClips(original.attributes, original.subtitles) : false
  const originalTranscript = original ? getTranscript(original) : undefined
  const allowOriginalEditor = !!original && !!originalTranscript && getAllowLinkToEditor(original.attributes.isDisabled, originalTranscript.attributes)
  const sourceLanguageCount = original ? original.subtitles.length : 0
  const sourceDoneLanguageCount = original ? getDoneLanguagesCount(original) : 0
  const hasNoClips = readyClips.length === 0 && pendingClips.length === 0
  const shouldRenderClipsGallery = clips.length > 0 || shouldAllowGeneratingClips

  const openSetupProjectOrRenewSubscriptionPopup = () => {
    if (!original) { return }

    props.handleOpenSetupProjectOrRenewSubscriptionPopup(original.id, 'repurpose')
  }

  const openOriginalEditor = () => {
    if (!original) { return }

    openProjectVersionEditor(original)
  }

  const openOriginalEditorFromKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (!original) { return }

    openProjectVersionEditorFromKeyDown(event, original)
  }

  const openOriginalLanguages = () => {
    if (!original) { return }

    props.resetOpenedProject()
    props.setOpenedProjectVersionId(original.id)
  }

  const renderTableHeader = () => {
    return (
      <tr>
        <th className="ProjectVersionsTable__preview" aria-hidden="true" />
        <th className="ProjectVersionsTable__title">
          Title
        </th>
        <th className="ProjectVersionsTable__duration">
          Duration
        </th>
        <th className="ProjectVersionsTable__status">
          Status
        </th>
        <th className="ProjectVersionsTable__languages">
          Languages
        </th>
        <th className="ProjectVersionsTable__more" />
      </tr>
    )
  }

  const renderProjectVersionRow = (
    projectVersion: IProjectVersion,
    index: number,
    isOriginal: boolean,
  ) => (
    <ProjectVersionRow
      key={projectVersion.id}
      index={index}
      isOriginal={isOriginal}
      sourceThumbnailUrl={sourceThumbnailUrl}
      projectVersion={projectVersion}
      resetOpenedProject={props.resetOpenedProject}
      setOpenedProjectVersionId={props.setOpenedProjectVersionId}
      handleOpenRenameProjectVersionPopup={props.handleOpenRenameProjectVersionPopup}
      handleOpenDeleteProjectVersionPopup={props.handleOpenDeleteProjectVersionPopup}
      handleOpenSetupProjectOrRenewSubscriptionPopup={props.handleOpenSetupProjectOrRenewSubscriptionPopup}
    />
  )

  const renderProjectVersionsRowsTable = () => {
    let rowIndex = 0

    return (
      <table>
        <thead>
          {renderTableHeader()}
        </thead>
        <tbody
          className={classnames(
            'ProjectVersionsTable__body',
            { 'ProjectVersionsTable__body-full': fullProjectVersionsView }
          )}
        >
          {original && renderProjectVersionRow(original, rowIndex++, true)}
          {map(pendingClips, clip =>
            renderProjectVersionRow(clip, rowIndex++, false)
          )}
          {map(readyClips, clip =>
            renderProjectVersionRow(clip, rowIndex++, false)
          )}
        </tbody>
      </table>
    )
  }

  const renderSourceVideo = () => {
    if (!original) { return null }

    return (
      <section className="ProjectVersionsTable__source">
        <div
          className={classnames(
            'ProjectVersionsTable__source-media',
            { 'ProjectVersionsTable__source-media-clickable': allowOriginalEditor }
          )}
          onClick={allowOriginalEditor ? openOriginalEditor : undefined}
          onKeyDown={allowOriginalEditor ? openOriginalEditorFromKeyDown : undefined}
          role={allowOriginalEditor ? 'button' : undefined}
          tabIndex={allowOriginalEditor ? 0 : undefined}
          data-testid={allowOriginalEditor ? `project-version-open-editor-card-source-${original.id}` : undefined}
        >
          {sourceThumbnailUrl ? (
            <img src={sourceThumbnailUrl} alt="" />
          ) : (
            <span className="ProjectVersionsTable__source-placeholder" aria-hidden="true">
              <Video />
            </span>
          )}
        </div>
        <div className="ProjectVersionsTable__source-copy">
          <div className="ProjectVersionsTable__source-label">
            Original video
          </div>
          <h2 className="ProjectVersionsTable__source-title" title={getCleanDisplayTitle(original.attributes.title)}>
            {getCleanDisplayTitle(original.attributes.title)}
          </h2>
          <div className="ProjectVersionsTable__source-meta">
            <span>{getFormattedDuration(original.attributes.duration)}</span>
            <button
              type="button"
              className="ProjectVersionsTable__source-languages"
              onClick={openOriginalLanguages}
              data-testid={`project-version-open-languages-source-${original.id}`}
            >
              {sourceDoneLanguageCount}/{sourceLanguageCount} languages
            </button>
          </div>
        </div>
        <div className="ProjectVersionsTable__source-actions">
          {allowOriginalEditor && (
            <Button
              caption="Open editor"
              styleType="gray-subtle"
              size="large"
              onClick={openOriginalEditor}
              data-testid={`project-version-open-editor-source-action-${original.id}`}
            />
          )}
          {shouldAllowGeneratingClips && (
            <Button
              caption="Generate more clips"
              styleType="brand"
              size="large"
              icon={<Magic />}
              onClick={openSetupProjectOrRenewSubscriptionPopup}
              data-testid={`project-version-generate-more-clips-${original.id}`}
            />
          )}
        </div>
      </section>
    )
  }

  return (
    <div
      className={classnames(
        'ProjectVersionsTable',
        { 'ProjectVersionsTable__full': fullProjectVersionsView },
        { 'ProjectVersionsTable__half': !fullProjectVersionsView }
      )}
    >
      {!shouldRenderClipsGallery && renderProjectVersionsRowsTable()}
      {shouldRenderClipsGallery && (
        <React.Fragment>
          {renderSourceVideo()}
          <section className="ProjectVersionsTable__clips-section">
            <div className="ProjectVersionsTable__clips-header">
              <div>
                <h2>Generated clips</h2>
                <p>{readyClips.length} ready{pendingClips.length > 0 ? `, ${pendingClips.length} generating` : ''}</p>
              </div>
            </div>
            {pendingClips.length > 0 && (
              <div className="ProjectVersionsTable__generating-note" role="status">
                <span className="ProjectVersionsTable__generating-note-icon" aria-hidden="true">
                  <Magic />
                </span>
                <p>
                  Your clips are being crafted with our most advanced model. This usually takes
                  a few minutes - feel free to leave this page, we&apos;ll keep working and your
                  clips will appear here as soon as they&apos;re ready.
                </p>
              </div>
            )}
            {hasNoClips ? (
              <div className="ProjectVersionsTable__clips-empty">
                <span className="ProjectVersionsTable__clips-empty-icon" aria-hidden="true">
                  <Magic />
                </span>
                <h3>No clips yet</h3>
                <p>
                  Turn this video into short, vertical clips - our model watches and listens to
                  find the best moments.
                </p>
                {original && shouldAllowGeneratingClips && (
                  <Button
                    caption="Generate clips"
                    styleType="brand"
                    size="large"
                    icon={<Magic />}
                    onClick={openSetupProjectOrRenewSubscriptionPopup}
                    data-testid={`project-version-generate-clips-empty-${original.id}`}
                  />
                )}
              </div>
            ) : (
              <div
                className="ProjectVersionsTable__clips-grid"
                aria-live="polite"
              >
                {map(pendingClips, (_clip, index) => (
                  <GeneratingClipCard
                    key={_clip.id}
                    index={index}
                    sourceThumbnailUrl={sourceThumbnailUrl}
                  />
                ))}
                {map(readyClips, (clip, index) => (
                  <ClipCard
                    key={clip.id}
                    {...props}
                    projectVersion={clip}
                    index={index}
                    sourceThumbnailUrl={sourceThumbnailUrl}
                    duplicatedThumbnailUrl={!!clip.attributes.thumbnailUrl && duplicatedThumbnailUrls[clip.attributes.thumbnailUrl] > 1}
                  />
                ))}
              </div>
            )}
          </section>
        </React.Fragment>
      )}
    </div>
  )
}

export default ProjectVersionsTable
